import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon, Schedule as ScheduleIcon, LocationOn as LocationIcon, People as PeopleIcon, Repeat as RepeatIcon } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createLogger } from "../utils/logger";
import { CalendarEvent, calendarService } from "../services/CalendarService";
import { TimezoneUtils } from "../utils/timezoneUtils";
import { getClassificationOptions } from "../config/classification";
import { TimezoneSelector } from "./TimezoneSelector";

interface EventEditModalProps {
  open: boolean;
  onClose: () => void;
  event?: CalendarEvent | null;
  onSave: (event: CalendarEvent) => void;
  onDelete?: (eventId: string) => void;
  displayTimezone?: string;
  selectedSlot?: { start: Date; end: Date } | null;
  onScopeSelection?: (scope: "instance" | "future" | "series", eventData: any) => void;
}

interface RecurringEventInfo {
  isRecurring: boolean;
  isInstance: boolean;
  baseEventId?: string;
  isException?: boolean;
  seriesTitle?: string;
}

interface RecurrenceRule {
  frequency: "daily" | "weekly" | "monthly" | "yearly";
  interval?: number;
  endDate?: Date;
  endCount?: number;
  daysOfWeek?: number[];
}

const logger = createLogger("EventEditModal");

// Helper function to format recurrence rules for display
const formatRecurrenceRule = (recurrence: RecurrenceRule): string => {
  if (!recurrence) return "";

  const { frequency, interval, endDate, endCount, daysOfWeek } = recurrence;

  let rule = "";

  // Frequency and interval
  if (interval && interval > 1) {
    rule += `Every ${interval} ${frequency}`;
  } else {
    rule += `Every ${frequency}`;
  }

  // Days of week for weekly recurrence
  if (frequency === "weekly" && daysOfWeek && daysOfWeek.length > 0) {
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const selectedDays = daysOfWeek.map((day) => dayNames[day]).join(", ");
    rule += ` on ${selectedDays}`;
  }

  // End condition
  if (endDate) {
    rule += ` until ${endDate.toLocaleDateString()}`;
  } else if (endCount) {
    rule += ` for ${endCount} occurrences`;
  }

  return rule;
};

export const EventEditModal: React.FC<EventEditModalProps> = ({ open, onClose, event, onSave, onDelete, displayTimezone, selectedSlot, onScopeSelection }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [endTime, setEndTime] = useState<Date>(new Date(Date.now() + 60 * 60 * 1000));
  const [location, setLocation] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [newAttendee, setNewAttendee] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceRule>({
    frequency: "weekly",
    interval: 1,
  });
  const [classification, setClassification] = useState<string | undefined>(undefined);
  const [eventTimezone, setEventTimezone] = useState<string>("UTC");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Recurring event handling
  const [recurringInfo, setRecurringInfo] = useState<RecurringEventInfo>({
    isRecurring: false,
    isInstance: false,
    baseEventId: undefined,
    isException: false,
    seriesTitle: undefined,
  });
  const [editScope, setEditScope] = useState<"instance" | "future" | "series">("instance");
  const [respondingToInvitation, setRespondingToInvitation] = useState(false);

  const isEditMode = !!event;

  // Handle all-day toggle
  useEffect(() => {
    if (allDay) {
      // When switching to all-day, set times to start/end of day
      const startOfDay = new Date(startTime);
      startOfDay.setHours(0, 0, 0, 0);
      setStartTime(startOfDay);

      // For all-day events, end time should be 00:00 of the next day (exclusive boundary)
      // But if this is an existing all-day event being loaded, we need to adjust it for display
      const endOfNextDay = new Date(endTime);

      // If this is an existing all-day event (event exists and is all-day),
      // the endTime is already stored as 00:00 of the next day, so we need to subtract 1 day for display
      if (event && event.allDay) {
        // This is an existing all-day event being loaded - adjust for display
        endOfNextDay.setDate(endOfNextDay.getDate() - 1);
        endOfNextDay.setHours(0, 0, 0, 0);
        console.log("EventEditModal: Loading existing all-day event - adjusting end time for display", {
          originalEndTime: endTime,
          adjustedEndTime: endOfNextDay,
          eventId: event.id,
          eventTitle: event.title,
        });
      } else {
        // This is a new all-day event - set to next day
        endOfNextDay.setDate(endOfNextDay.getDate() + 1);
        endOfNextDay.setHours(0, 0, 0, 0);
        console.log("EventEditModal: Creating new all-day event - setting end time to next day", {
          originalEndTime: endTime,
          adjustedEndTime: endOfNextDay,
        });
      }

      // Use setTimeout to ensure the state update happens after the current render cycle
      setTimeout(() => {
        setEndTime(endOfNextDay);
        console.log("EventEditModal: Applied end time adjustment", {
          finalEndTime: endOfNextDay,
          finalEndTimeString: endOfNextDay.toISOString(),
        });
      }, 0);
    } else {
      // When switching from all-day, ensure end time is after start time
      if (endTime <= startTime) {
        const newEndTime = new Date(startTime);
        newEndTime.setHours(startTime.getHours() + 1);
        setEndTime(newEndTime);
      }
    }
  }, [allDay, event]);

  // Handle all-day event end time adjustment after event is loaded
  useEffect(() => {
    if (event && event.allDay && allDay && endTime) {
      console.log("EventEditModal: Post-load all-day adjustment check", {
        eventId: event.id,
        eventTitle: event.title,
        currentEndTime: endTime,
        eventEndTime: event.endTime,
        allDay: allDay,
      });

      // Check if the end time needs adjustment for display
      const eventEndDate = new Date(event.endTime);
      const currentEndDate = new Date(endTime);

      // If the current end time matches the event's end time (which is stored as next day),
      // we need to adjust it for display
      if (currentEndDate.getTime() === eventEndDate.getTime()) {
        const adjustedEndTime = new Date(currentEndDate);
        adjustedEndTime.setDate(adjustedEndTime.getDate() - 1);
        adjustedEndTime.setHours(0, 0, 0, 0);

        console.log("EventEditModal: Applying post-load all-day adjustment", {
          originalEndTime: currentEndDate,
          adjustedEndTime: adjustedEndTime,
        });

        setEndTime(adjustedEndTime);
      }
    }
  }, [event, allDay, endTime]);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description || "");
      console.log("EventEditModal: Loading event:", event);

      // Ensure we have valid Date objects
      const startDate = new Date(event.startTime);
      const endDate = new Date(event.endTime);

      if (isNaN(startDate.getTime())) {
        console.error("Invalid start time for event:", event.startTime);
        setError("Invalid start time for event");
        return;
      }

      if (isNaN(endDate.getTime())) {
        console.error("Invalid end time for event:", event.endTime);
        setError("Invalid end time for event");
        return;
      }

      setStartTime(startDate);
      setEndTime(endDate);
      setLocation(event.location || "");
      setClassification((event as any).classification);
      setAllDay(event.allDay);
      setAttendees(event.attendees?.map((a) => a.email) || []);
      setEventTimezone((event as any).timezone || "UTC");

      // Handle recurring event properties
      const isRecurringEvent = !!(event as any).recurrence || !!(event as any).baseEventId;
      const isInstance = !!(event as any).baseEventId;
      const isException = !!(event as any).isException;

      setRecurringInfo({
        isRecurring: isRecurringEvent,
        isInstance: isInstance,
        baseEventId: (event as any).baseEventId,
        isException: isException,
        seriesTitle: isInstance ? (event as any).seriesTitle : undefined,
      });

      // Default to editing instance for recurring events
      if (isRecurringEvent) {
        setEditScope("instance");
      }

      // Handle recurrence rules
      if ((event as any).recurrence) {
        setIsRecurring(true);
        setRecurrence((event as any).recurrence);
      }
    } else {
      // Reset form for new event
      setTitle("");
      setDescription("");

      // Use selectedSlot times if available, otherwise use current time
      if (selectedSlot) {
        logger.debug(`Modal received selectedSlot: ${selectedSlot.start.toLocaleString()} to ${selectedSlot.end.toLocaleString()} | tz=${displayTimezone}`);
        setStartTime(selectedSlot.start);
        setEndTime(selectedSlot.end);
      } else {
        setStartTime(new Date());
        setEndTime(new Date(Date.now() + 60 * 60 * 1000));
      }

      setLocation("");
      setClassification(undefined);
      setAllDay(false);
      setAttendees([]);
      setIsRecurring(false);
      setRecurrence({ frequency: "weekly", interval: 1 });
      setEventTimezone(displayTimezone || "UTC");

      // Reset recurring info
      setRecurringInfo({
        isRecurring: false,
        isInstance: false,
        baseEventId: undefined,
        isException: false,
        seriesTitle: undefined,
      });
      setEditScope("instance");
    }
    setError("");
  }, [event, open, selectedSlot]);

  // Helper function to validate email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddAttendee = () => {
    const trimmedInput = newAttendee.trim();
    if (trimmedInput) {
      // Split by comma or semicolon and process each email
      const emails = trimmedInput
        .split(/[,;]/)
        .map((email) => email.trim())
        .filter((email) => email);
      const validEmails = emails.filter((email) => isValidEmail(email));
      const newEmails = validEmails.filter((email) => !attendees.includes(email));

      if (newEmails.length > 0) {
        setAttendees([...attendees, ...newEmails]);
        setNewAttendee("");
      }
    }
  };

  const handleRemoveAttendee = (email: string) => {
    setAttendees(attendees.filter((a) => a !== email));
  };

  // Handle input blur - automatically add valid emails
  const handleAttendeeInputBlur = () => {
    const trimmedInput = newAttendee.trim();
    if (trimmedInput) {
      // Split by comma or semicolon and process each email
      const emails = trimmedInput
        .split(/[,;]/)
        .map((email) => email.trim())
        .filter((email) => email);
      const validEmails = emails.filter((email) => isValidEmail(email));
      const newEmails = validEmails.filter((email) => !attendees.includes(email));

      if (newEmails.length > 0) {
        setAttendees([...attendees, ...newEmails]);
        setNewAttendee("");
      }
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    if (allDay) {
      // For all-day events, compare dates only (allow same day for single-day events)
      const startDate = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
      const endDate = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate());

      if (startDate > endDate) {
        setError("End date must be the same day or later for all-day events");
        return;
      }
    } else {
      // For regular events, end time must be after start time
      if (startTime >= endTime) {
        setError("End time must be after start time");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      // For all-day events, adjust the end time for saving (add 1 day)
      let adjustedEndTime = endTime;
      if (allDay) {
        // Ensure we have a valid Date object
        const endDate = new Date(endTime);
        if (isNaN(endDate.getTime())) {
          throw new Error("Invalid end date for all-day event");
        }
        adjustedEndTime = new Date(endDate);
        adjustedEndTime.setDate(adjustedEndTime.getDate() + 1);

        // Validate the adjusted date
        if (isNaN(adjustedEndTime.getTime())) {
          throw new Error("Invalid adjusted end date for all-day event");
        }
      }

      const eventData = {
        title: title.trim(),
        description: description.trim(),
        startTime: startTime,
        endTime: adjustedEndTime,
        allDay,
        location: location.trim(),
        timezone: eventTimezone || displayTimezone || "UTC", // Use selected event timezone
        attendees: attendees.map((email) => ({ email, displayName: email })),
        classification,
        ...(isRecurring && { recurrence }),
      };

      // Debug logging for all-day events
      if (allDay) {
        console.log("All-day event data:", {
          originalEndTime: endTime,
          adjustedEndTime: adjustedEndTime,
          startTime: startTime,
          endTimeType: typeof adjustedEndTime,
          endTimeIsDate: adjustedEndTime instanceof Date,
          endTimeValid: !isNaN(adjustedEndTime.getTime()),
        });
      }

      console.log("Event data: ", eventData);

      let savedEvent: CalendarEvent;

      if (isEditMode && event) {
        // If onScopeSelection is provided and this is a recurring event, use scope modal approach
        if (onScopeSelection && recurringInfo.isRecurring) {
          console.log("EventEditModal: Using scope modal for recurring event", {
            isRecurring: recurringInfo.isRecurring,
            isInstance: recurringInfo.isInstance,
            baseEventId: recurringInfo.baseEventId,
          });
          onScopeSelection("instance", eventData);
          onClose();
          return;
        }

        // Otherwise, use the existing logic for backward compatibility
        console.log("EventEditModal: Using direct update approach", {
          isRecurring: recurringInfo.isRecurring,
          hasOnScopeSelection: !!onScopeSelection,
        });

        // Handle recurring event updates
        if (recurringInfo.isRecurring) {
          if (editScope === "instance" && recurringInfo.isInstance) {
            // Update individual instance
            console.log("Updating individual instance");
            savedEvent = await calendarService.updateEventInstance(event.id, eventData);
          } else if (editScope === "future" && recurringInfo.isInstance) {
            // Update this and future instances
            console.log("Updating this and future instances");
            const result = await calendarService.updateEventFutureInstances(event.id, eventData);
            savedEvent = result.newBaseEvent;
          } else if (editScope === "series" && recurringInfo.baseEventId) {
            // Update entire series
            console.log("Updating entire series");
            savedEvent = await calendarService.updateEventSeries(recurringInfo.baseEventId, eventData);
          } else {
            // Fallback to regular update
            console.log("Updating regular event -recurring");
            savedEvent = await calendarService.updateEvent(event.id, eventData);
          }
        } else {
          // Regular event update
          console.log("Updating regular event");
          savedEvent = await calendarService.updateEvent(event.id, eventData);
        }
      } else {
        console.log("Creating new event");
        savedEvent = await calendarService.createEvent(eventData);
      }

      onSave(savedEvent);
      try {
        const { analyticsService } = require("../services/AnalyticsService");
        if ((event as any)?.classification !== classification) {
          analyticsService.logUserInteraction("calendar", "EventEditModal", "Calendar.Entry.Classified", classification || "auto");
        }
      } catch (error) {
        // Analytics error - don't block the save operation
        console.warn("Failed to log analytics:", error);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save event");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event || !onDelete) return;

    setLoading(true);
    setError("");

    try {
      // Handle recurring event deletion
      if (recurringInfo.isRecurring) {
        if (editScope === "instance" && recurringInfo.isInstance) {
          // Delete individual instance
          await calendarService.deleteEventInstance(event.id);
        } else if (editScope === "future" && recurringInfo.isInstance) {
          // Delete this and future instances
          await calendarService.deleteEventFutureInstances(event.id);
        } else if (editScope === "series" && recurringInfo.baseEventId) {
          // Delete entire series
          await calendarService.deleteEventSeries(recurringInfo.baseEventId);
        } else {
          // Fallback to regular delete
          await calendarService.deleteEvent(event.id);
        }
      } else {
        // Regular event deletion
        await calendarService.deleteEvent(event.id);
      }

      onDelete(event.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete event");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{isEditMode ? "Edit Event" : "Create New Event"}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {recurringInfo.isRecurring && (
          <Box sx={{ mt: 1 }}>
            <Chip
              icon={<RepeatIcon />}
              label={recurringInfo.isInstance ? "Recurring Event Instance" : "Recurring Event Series"}
              color={recurringInfo.isException ? "warning" : "primary"}
              size="small"
            />
            {recurrence && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                {formatRecurrenceRule(recurrence)}
              </Typography>
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Recurring Event Edit Scope Selector */}
        {recurringInfo.isRecurring && isEditMode && (
          <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Edit Scope
            </Typography>
            <FormControl component="fieldset">
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant={editScope === "instance" ? "contained" : "outlined"} size="small" onClick={() => setEditScope("instance")}>
                  This Event Only
                </Button>
                <Button variant={editScope === "future" ? "contained" : "outlined"} size="small" onClick={() => setEditScope("future")}>
                  This and Future
                </Button>
                <Button variant={editScope === "series" ? "contained" : "outlined"} size="small" onClick={() => setEditScope("series")}>
                  All Events in Series
                </Button>
              </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {editScope === "instance"
                ? "Changes will only affect this specific occurrence"
                : editScope === "future"
                  ? "Changes will affect this occurrence and all future occurrences"
                  : "Changes will affect all occurrences in the series (past and future)"}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Title */}
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required placeholder="Event title" disabled={(event as any)?.isInvited} />

          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Event description"
            disabled={(event as any)?.isInvited}
          />

          {/* Date and Time */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <FormControlLabel control={<Switch checked={allDay} onChange={(e) => setAllDay(e.target.checked)} disabled={(event as any)?.isInvited} />} label="All day" />
            </Box>

            {!allDay && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <DateTimePicker
                    label={`Start time`}
                    value={startTime}
                    onChange={(newValue) => {
                      if (newValue) {
                        setStartTime(newValue);
                      }
                    }}
                    disabled={(event as any)?.isInvited}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DateTimePicker
                    label={`End time`}
                    value={endTime}
                    onChange={(newValue) => {
                      if (newValue) {
                        setEndTime(newValue);
                      }
                    }}
                    disabled={(event as any)?.isInvited}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <Box sx={{ minWidth: 200 }}>
                    <TimezoneSelector value={eventTimezone} onChange={setEventTimezone} autoDetect={false} label="Timezone" fullWidth={true} />
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl fullWidth disabled={(event as any)?.isInvited}>
                    <InputLabel id="classification-label">Classification</InputLabel>
                    <Select labelId="classification-label" label="Classification" value={classification || ""} onChange={(e) => setClassification(e.target.value || undefined)}>
                      <MenuItem value="">
                        <em>Auto</em>
                      </MenuItem>
                      {getClassificationOptions().map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {allDay && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label={`Start date${displayTimezone ? ` (${displayTimezone})` : ""}`}
                    type="date"
                    value={(() => {
                      try {
                        if (!startTime || typeof startTime.getFullYear !== "function" || isNaN(startTime.getTime())) {
                          return "";
                        }
                        return TimezoneUtils.convertToString(startTime).split("T")[0];
                      } catch (error) {
                        console.error("Error formatting start date:", error);
                        return "";
                      }
                    })()}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        newDate.setHours(startTime.getHours(), startTime.getMinutes());
                        setStartTime(newDate);
                      }
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={`End date${displayTimezone ? ` (${displayTimezone})` : ""}`}
                    type="date"
                    value={(() => {
                      try {
                        if (!endTime || typeof endTime.getFullYear !== "function" || isNaN(endTime.getTime())) {
                          return "";
                        }
                        return TimezoneUtils.convertToString(endTime).split("T")[0];
                      } catch (error) {
                        console.error("Error formatting end date:", error);
                        return "";
                      }
                    })()}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      if (!isNaN(newDate.getTime())) {
                        newDate.setHours(endTime.getHours(), endTime.getMinutes());
                        setEndTime(newDate);
                      }
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                {displayTimezone && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                    Dates are shown in {displayTimezone} timezone
                  </Typography>
                )}
              </Box>
            )}
          </LocalizationProvider>

          {/* Location */}
          <TextField
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            fullWidth
            placeholder="Event location"
            disabled={(event as any)?.isInvited}
            InputProps={{
              startAdornment: <LocationIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />

          {/* Attendees */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center" }}>
              <PeopleIcon sx={{ mr: 1 }} />
              Attendees
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Add attendee email"
                value={newAttendee}
                onChange={(e) => setNewAttendee(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddAttendee()}
                onBlur={handleAttendeeInputBlur}
                disabled={(event as any)?.isInvited}
                sx={{ flexGrow: 1 }}
                helperText={newAttendee && !newAttendee.split(/[,;]/).some((email) => isValidEmail(email.trim())) ? "Please enter valid email addresses (separate multiple with commas)" : ""}
                error={!!(newAttendee && !newAttendee.split(/[,;]/).some((email) => isValidEmail(email.trim())))}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddAttendee}
                disabled={(event as any)?.isInvited || !newAttendee.trim() || !newAttendee.split(/[,;]/).some((email) => isValidEmail(email.trim()))}
              >
                <AddIcon />
              </Button>
            </Box>

            {attendees.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {attendees.map((email) => (
                  <Chip key={email} label={email} onDelete={(event as any)?.isInvited ? undefined : () => handleRemoveAttendee(email)} size="small" />
                ))}
              </Box>
            )}
          </Box>

          {/* Recurrence */}
          <Box>
            <FormControlLabel
              control={<Switch checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} disabled={(event as any)?.isInvited} />}
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <RepeatIcon sx={{ mr: 1 }} />
                  Recurring event
                </Box>
              }
            />

            {isRecurring && (
              <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 120 }} disabled={(event as any)?.isInvited}>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={recurrence.frequency}
                    onChange={(e) =>
                      setRecurrence({
                        ...recurrence,
                        frequency: e.target.value as any,
                      })
                    }
                    label="Frequency"
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Interval"
                  type="number"
                  value={recurrence.interval || 1}
                  onChange={(e) =>
                    setRecurrence({
                      ...recurrence,
                      interval: parseInt(e.target.value) || 1,
                    })
                  }
                  disabled={(event as any)?.isInvited}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1, max: 99 }}
                />

                <TextField
                  size="small"
                  label="End after"
                  type="number"
                  value={recurrence.endCount || ""}
                  onChange={(e) =>
                    setRecurrence({
                      ...recurrence,
                      endCount: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  sx={{ width: 120 }}
                  placeholder="Occurrences"
                  inputProps={{ min: 1 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {!(event as any)?.isInvited && (
          <>
            {isEditMode && onDelete && (
              <Button onClick={handleDelete} color="error" disabled={loading} startIcon={<DeleteIcon />}>
                Delete
              </Button>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>

            <Button onClick={handleSave} variant="contained" disabled={loading || !title.trim()} startIcon={loading ? <CircularProgress size={16} /> : undefined}>
              {loading ? "Saving..." : isEditMode ? "Update" : "Create"}
            </Button>
          </>
        )}

        {(event as any)?.isInvited && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={onClose} disabled={loading}>
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
